import { CSSProperties } from 'react'
import styled, { css, Interpolation } from 'styled-components'

import { Typography as CommonTypography } from 'primsell-shared-layer/components'
import { TableThemeE } from 'primsell-shared-layer/types'

import { SortIcon as CommonSortIcon } from 'primsell-shared-layer/assets/icons'

type TableConfigT = Record<string, string | Partial<CSSProperties>>
interface ITableProps {
  tabletheme: TableThemeE
  hideBorder?: boolean
  isTableDisabled?: boolean
  isOneRow?: boolean
}

interface ITableContainerProps {
  isTableDisabled?: boolean
  isLinkExistForScrolling?: boolean
}

export const TableContainer = styled.div<ITableContainerProps>`
  opacity: ${({ isTableDisabled }) => (isTableDisabled ? 0.5 : 1)};
  pointer-events: ${({ isTableDisabled }) => (isTableDisabled ? 'none' : 'auto')};

  ${({ isLinkExistForScrolling }) =>
    isLinkExistForScrolling
      ? css`
          height: 250px;
          background-color: #fff;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        `
      : css`
          overflow: auto;
          height: 100%;
        `}
`

const TablewrapperConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    borderRadius: '8px',
  },
  [TableThemeE.SECONDARY]: {
    backgroundColor: '#fff',
    border: '8px solid #fff',
    borderRadius: '16px',
  },
}

export const Tablewrapper = styled.div<ITableProps>`
  width: 100%;
  border: none;
  cursor: default;
  overflow-x: auto;

  ${({ tabletheme }) => TablewrapperConfig[tabletheme]};
  ${({ hideBorder }) =>
    hideBorder &&
    css`
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
      border-bottom: 0px;
    `}
`

export const TableInfiniteScrollingContainer = styled(Tablewrapper)`
  overflow: auto;
  height: 100%;
`

export const Table = styled.table<ITableProps>`
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
  width: 100%;
`

export const TableHead = styled.thead`
  background-color: #f1f1f1;
  position: sticky;
  top: -1px;
`

const TableRowConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    backgroundColor: '#fff',
  },
  [TableThemeE.SECONDARY]: {
    '&:nth-child(odd)': {
      backgroundColor: '#fff',
    },
    '&:nth-child(even)': {
      backgroundColor: '#F9FBFD',
    },
  },
}

export const TableRow = styled.tr<ITableProps>`
  ${({ tabletheme }) => css`
    ${TableRowConfig[tabletheme]}
  `}
  ${({ tabletheme }) => RowTextConfig[tabletheme]};
`

const TableCellConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    borderRight: '1px solid #f3f4f6',
    borderBottom: '1px solid #f3f4f6',
    padding: '16px 20px',
  },
  [TableThemeE.SECONDARY]: {
    height: '50px',
    verticalAlign: 'middle',
    borderRight: '1px solid #DBE0EB',
    padding: '14px 16px',
  },
}

export const TableCell = styled.td<ITableProps>`
  height: 50px;
  vertical-align: middle;

  ${({ tabletheme }) => TableCellConfig[tabletheme]};

  &:last-child {
    border-right: none;
  }

  ${({ isOneRow }) =>
    isOneRow &&
    css`
      height: 52px;
      padding-bottom: 16px;
    `}
`
interface ITableCellContainerProps {
  cellStyle?: Interpolation<CSSProperties>
}

export const TableCellContainer = styled.div<ITableCellContainerProps>`
  ${({ cellStyle }) => cellStyle && cellStyle};
  overflow: hidden;
`

const TableHeaderCellConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    borderRight: '1px solid #f3f4f6',
    padding: '16px 20px',
    textAlign: 'left',
    backgroundColor: '#fff',
    '&::before': {
      backgroundColor: '#f3f4f6',
    },
  },
  [TableThemeE.SECONDARY]: {
    height: '60px',
    verticalAlign: 'middle',
    borderRight: '1px solid #DBE0EB',
    padding: '20px 16px  22px 16px',
    textAlign: 'center',
    backgroundColor: '#f9fbfd',
    '&:first-child': {
      borderTopLeftRadius: '12px',
    },
    '&:last-child': {
      borderTopRightRadius: '12px',
    },
    '&::before': {
      backgroundColor: '#dbe0eb',
    },
  },
}

export const TableHeaderCell = styled.th<ITableProps>`
  position: relative;

  ${({ tabletheme }) => TableHeaderCellConfig[tabletheme]};

  &:last-child {
    border-right: none;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
  }
`

const LabelConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    color: '#484964',
    fontWeight: '400',
  },
  [TableThemeE.SECONDARY]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#3c3c43',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
}

export const Label = styled(CommonTypography)<ITableProps>`
  font-size: 14px;
  ${({ tabletheme }) => LabelConfig[tabletheme]};
`

export const SortIcon = styled(CommonSortIcon)`
  margin-left: 4px;
  cursor: pointer;
`

const RowTextConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    color: '#808192',
    fontWeight: '400',
  },
  [TableThemeE.SECONDARY]: {
    color: '#3c3c43',
    fontStyle: 'normal',
    fontWeight: '500',
  },
}

export const tooltipStyle: CSSProperties = { textTransform: 'none' }

const VievMoreButtonConfig: Record<TableThemeE, TableConfigT> = {
  [TableThemeE.PRIMARY]: {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  [TableThemeE.SECONDARY]: {
    paddingTop: '14px',
    paddingBottom: '14px',
  },
}

export const ViewMoreButton = styled.button<ITableProps>`
  width: 100%;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  background-color: #d7e5fd;
  ${({ tabletheme }) => VievMoreButtonConfig[tabletheme]};
`

export const Typography = styled(CommonTypography)`
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  color: #2c72db;
`
